// InsightsDashboard — Universal multi-domain dashboard.
//
// Architecture (per user spec):
//   • Backend (insights_registry.py)  →  brain (catalog + display config)
//   • This page                       →  pure renderer
//
// Flow:
//   1. GET /api/insights              → catalog grouped by domain
//   2. POST /api/insights/_batch      → all insights resolved in one shot
//   3. Render via <InsightRenderer />
//
// Time filter (7d / 30d / 90d / all) is the only user input. Insights
// without a ``date_field`` ignore the range — backend handles that.
//
// 2026-02 refresh: full English localisation (with Arabic fallback via i18n)
// and a richer domain palette so each section is visually distinguishable.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../App';
import InsightRenderer from '../components/insights/InsightRenderer';
import { RefreshCw, Calendar, Sparkles } from 'lucide-react';

// Domain-aware accent palette. Each section gets a band-colour so the
// page reads as four distinct neighbourhoods rather than one long list.
const DOMAIN_ACCENTS = {
  finance:    { dot: 'bg-emerald-500', text: 'text-emerald-700', band: 'from-emerald-50 to-white',  border: 'border-emerald-100' },
  sales:      { dot: 'bg-sky-500',     text: 'text-sky-700',     band: 'from-sky-50 to-white',      border: 'border-sky-100' },
  projects:   { dot: 'bg-violet-500',  text: 'text-violet-700',  band: 'from-violet-50 to-white',   border: 'border-violet-100' },
  operations: { dot: 'bg-amber-500',   text: 'text-amber-700',   band: 'from-amber-50 to-white',    border: 'border-amber-100' },
};

export default function InsightsDashboard() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const RANGE_OPTIONS = useMemo(() => ([
    { id: '7d',  label: isAr ? 'آخر 7 أيام' : 'Last 7 days' },
    { id: '30d', label: isAr ? 'آخر 30 يوم' : 'Last 30 days' },
    { id: '90d', label: isAr ? 'آخر 90 يوم' : 'Last 90 days' },
    { id: 'all', label: isAr ? 'كل الفترة' : 'All time' },
  ]), [isAr]);

  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [range, setRange] = useState('30d');
  const [data, setData] = useState({});       // key -> { insight | error }
  const [loading, setLoading] = useState({}); // key -> bool
  const [refreshTick, setRefreshTick] = useState(0);

  // Load the catalog once. Deferred via setTimeout so the initial
  // setState happens off the render cycle.
  useEffect(() => {
    let active = true;
    const handle = setTimeout(() => {
      axiosInstance
        .get('/insights')
        .then((res) => { if (active) setCatalog(res.data); })
        .catch((err) => { if (active) setCatalogError(err); });
    }, 0);
    return () => { active = false; clearTimeout(handle); };
  }, []);

  // Flatten all insight keys across all domains for fetching.
  const allKeys = useMemo(() => {
    if (!catalog?.domains) return [];
    return catalog.domains.flatMap((d) => d.insights.map((i) => i.key));
  }, [catalog]);

  // Fetch every insight in a SINGLE batched request.
  useEffect(() => {
    if (allKeys.length === 0) return;
    let active = true;

    const handle = setTimeout(() => {
      setLoading((prev) => {
        const next = { ...prev };
        allKeys.forEach((k) => { next[k] = true; });
        return next;
      });

      (async () => {
        try {
          const res = await axiosInstance.post('/insights/_batch', {
            keys: allKeys,
            since: range,
          });
          if (!active) return;
          const next = {};
          const nextLoading = {};
          for (const item of res.data?.results || []) {
            if (item.error) {
              next[item.key] = { error: new Error(item.error) };
            } else {
              next[item.key] = { insight: item };
            }
            nextLoading[item.key] = false;
          }
          setData((prev) => ({ ...prev, ...next }));
          setLoading((prev) => ({ ...prev, ...nextLoading }));
        } catch (err) {
          if (!active) return;
          const next = {};
          const nextLoading = {};
          for (const k of allKeys) {
            next[k] = { error: err };
            nextLoading[k] = false;
          }
          setData((prev) => ({ ...prev, ...next }));
          setLoading((prev) => ({ ...prev, ...nextLoading }));
        }
      })();
    }, 0);

    return () => { active = false; clearTimeout(handle); };
  }, [allKeys, range, refreshTick]);

  const handleRefresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  return (
    <div
      data-testid="insights-dashboard"
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 p-6"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-200">
            <Sparkles size={20} />
          </div>
          <div>
            <h1
              className="text-2xl font-bold text-slate-900 tracking-tight"
              data-testid="dashboard-title"
            >
              {isAr ? 'لوحة الرؤى الشاملة' : 'Insights Dashboard'}
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              {isAr
                ? 'تعرض جميع المؤشرات حسب النطاق الزمني المختار. كل المؤشرات تعتمد على نفس مصدر البيانات.'
                : 'All KPIs respond to the selected time range and share a single source of truth.'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Range filter */}
          <div
            className="flex items-center gap-1 bg-white border border-slate-200 rounded-xl p-1 shadow-sm"
            data-testid="range-filter"
          >
            <Calendar size={14} className="text-slate-400 mx-2" />
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.id}
                data-testid={`range-${r.id}`}
                onClick={() => setRange(r.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${
                  range === r.id
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <button
            onClick={handleRefresh}
            data-testid="refresh-btn"
            className="p-2.5 rounded-xl bg-white border border-slate-200 text-slate-500 hover:text-slate-700 hover:shadow-sm transition"
            title={isAr ? 'تحديث' : 'Refresh'}
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Catalog load error */}
      {catalogError && (
        <div
          className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-4 text-sm shadow-sm"
          data-testid="catalog-error"
        >
          {isAr
            ? 'تعذّر تحميل قائمة الرؤى. تحقق من الاتصال بالخادم.'
            : 'Unable to load the insights catalogue. Please check the server connection.'}
        </div>
      )}

      {/* Loading catalog */}
      {!catalog && !catalogError && (
        <div className="text-slate-400 text-sm">{isAr ? 'جاري التحميل…' : 'Loading…'}</div>
      )}

      {/* Domains */}
      {catalog?.domains?.map((domain) => {
        if (domain.insights.length === 0) return null;
        const accent = DOMAIN_ACCENTS[domain.id] || DOMAIN_ACCENTS.operations;
        const domainLabel = isAr ? domain.label : (domain.label_en || domain.label);

        return (
          <section
            key={domain.id}
            data-testid={`domain-${domain.id}`}
            className={`mb-8 rounded-2xl bg-gradient-to-br ${accent.band} border ${accent.border} p-5 shadow-sm`}
          >
            <div className="flex items-baseline gap-3 mb-4">
              <span className={`w-2.5 h-2.5 rounded-full ${accent.dot} shadow-sm`} />
              <h2 className={`text-lg font-bold ${accent.text} tracking-tight`}>
                {domainLabel}
              </h2>
              <span className="text-xs text-slate-400 font-medium">
                {domain.insights.length} {isAr ? 'مؤشر' : 'metrics'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {domain.insights.map((meta) => {
                const entry = data[meta.key] || {};
                const fetched = entry.insight;
                // Merge catalog meta with fetched data so the tile can
                // render its title/icon before data arrives.
                const insight = fetched
                  ? {
                      ...fetched,
                      title: isAr ? fetched.title : (fetched.title_en || fetched.title),
                      description: isAr ? fetched.description : (fetched.description_en || fetched.description),
                    }
                  : {
                      key: meta.key,
                      title: isAr ? meta.title : (meta.title_en || meta.title),
                      description: isAr ? meta.description : (meta.description_en || meta.description),
                      display: meta.display,
                      rows: [],
                    };
                return (
                  <InsightRenderer
                    key={meta.key}
                    insight={insight}
                    loading={!!loading[meta.key]}
                    error={entry.error}
                    isAr={isAr}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
